/**
 * PlCategoriesConfig.js
 * Deux sections :
 *   1. Catalogue des catégories Pennylane synchronisées (arbre famille → catégorie)
 *   2. Mappings ERP → catégorie (onglets Agences / Familles d'articles)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Check, Trash2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';

// ─── Utilitaires ─────────────────────────────────────────────────────────────
const DIRECTION_LABEL = { both: 'Les deux', cash_in: 'Encaissement', cash_out: 'Décaissement' };

function DirectionBadge({ dir }) {
  const colors = {
    both:     { bg: '#EFF6FF', color: '#1D4ED8' },
    cash_in:  { bg: '#DCFCE7', color: '#15803D' },
    cash_out: { bg: '#FEF3C7', color: '#92400E' },
  };
  const s = colors[dir] || colors.both;
  return (
    <span className="text-xs px-1.5 py-0.5 rounded-full font-medium"
      style={{ background: s.bg, color: s.color }}>
      {DIRECTION_LABEL[dir] || dir}
    </span>
  );
}

// ─── Onglet 1 : Catalogue ─────────────────────────────────────────────────────
function CatalogueTab({ societeId }) {
  const [cats,    setCats]    = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!societeId) return;
    setLoading(true);
    api.getPlCategories(societeId)
      .then(r => setCats(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [societeId]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    if (!societeId) { toast.error('Sélectionnez une société'); return; }
    setSyncing(true);
    try {
      const r = await api.syncPlCategories(societeId);
      toast.success(`Sync OK — ${r.data.nb_families} familles, ${r.data.nb_categories} catégories`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur sync'); }
    finally { setSyncing(false); }
  };

  const families  = cats.filter(c => c.type === 'family');
  const catsByParent = {};
  cats.filter(c => c.type === 'category').forEach(c => {
    if (!catsByParent[c.parent_id]) catsByParent[c.parent_id] = [];
    catsByParent[c.parent_id].push(c);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Catégories synchronisées depuis Pennylane. Le sync est hebdomadaire (lundi 03h00) ou manuel ci-dessous.
        </p>
        <button onClick={handleSync} disabled={syncing || !societeId}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
          style={{ background: 'var(--primary-solid)' }}>
          <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Sync…' : 'Synchroniser'}
        </button>
      </div>
      {loading ? (
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Chargement…</p>
      ) : families.length === 0 ? (
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Aucune catégorie — cliquez sur "Synchroniser" pour charger les catégories depuis Pennylane.
        </p>
      ) : (
        <div className="space-y-3">
          {families.map(fam => (
            <div key={fam.id} className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-2 px-4 py-2.5"
                style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                <span className="text-xs font-bold" style={{ color: 'var(--text)' }}>{fam.name}</span>
                <DirectionBadge dir={fam.direction} />
                <span className="text-xs ml-auto" style={{ color: 'var(--text-muted)' }}>
                  {(catsByParent[fam.id] || []).length} catégorie(s)
                </span>
              </div>
              <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
                {(catsByParent[fam.id] || []).map(cat => (
                  <div key={cat.id} className="flex items-center gap-2 px-4 py-2">
                    <ChevronRight size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    <span className="text-xs flex-1" style={{ color: 'var(--text)' }}>{cat.name}</span>
                    <DirectionBadge dir={cat.direction} />
                    <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>#{cat.pl_id}</span>
                  </div>
                ))}
                {(catsByParent[fam.id] || []).length === 0 && (
                  <div className="px-4 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>Aucune catégorie</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Onglet 2 : Mappings ──────────────────────────────────────────────────────
function MappingRow({ item, categories, onDelete }) {
  const cat = categories.find(c => c.id === item.category_id);
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg border"
      style={{ borderColor: 'var(--border)' }}>
      <span className="text-xs font-medium flex-1 truncate" style={{ color: 'var(--text)' }}>{item.source_name}</span>
      <ChevronRight size={12} style={{ color: 'var(--text-muted)' }} />
      <span className="text-xs font-semibold" style={{ color: 'var(--primary-solid)' }}>
        {item.category_name || (cat?.name) || `#${item.category_id}`}
      </span>
      {item.family_name && (
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>({item.family_name})</span>
      )}
      <button onClick={() => onDelete(item.id)}
        className="p-1 rounded hover:bg-red-50 transition-colors ml-1">
        <Trash2 size={12} style={{ color: '#EF4444' }} />
      </button>
    </div>
  );
}

function MappingsSection({ societeId, sourceType, sourceLabel, sources, categories, mappings, onRefresh }) {
  const [addSource, setAddSource]   = useState('');
  const [addCat,    setAddCat]      = useState('');
  const [addDir,    setAddDir]      = useState('both');
  const [saving,    setSaving]      = useState(false);

  const isFamille = sourceType === 'famille';

  const DIRECTIONS = [
    { value: 'both',     label: 'Les deux (ventes & achats)' },
    { value: 'cash_in',  label: 'Ventes uniquement' },
    { value: 'cash_out', label: 'Achats uniquement' },
  ];
  const DIR_BADGE = {
    both:     { label: 'Les deux',   bg: '#EFF6FF', color: '#1D4ED8' },
    cash_in:  { label: 'Ventes',     bg: '#DCFCE7', color: '#15803D' },
    cash_out: { label: 'Achats',     bg: '#FEF3C7', color: '#92400E' },
  };

  const handleAdd = async () => {
    if (!addSource || !addCat) { toast.error('Sélectionnez une source et une catégorie'); return; }
    setSaving(true);
    try {
      await api.upsertPlCategoryMapping({
        societe_id:  societeId,
        source_type: sourceType,
        source_id:   addSource,
        category_id: parseInt(addCat),
        direction:   isFamille ? addDir : 'both',
      });
      toast.success('Mapping enregistré');
      setAddSource(''); setAddCat(''); setAddDir('both');
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.deletePlCategoryMapping(id);
      toast.success('Mapping supprimé');
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const myMappings = mappings.filter(m => m.source_type === sourceType);

  // Enrichir avec le nom de la source
  const enriched = myMappings.map(m => ({
    ...m,
    source_name: sources.find(s => s.id === m.source_id)?.nom || m.source_id,
  }));

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
        {sourceLabel}
      </h4>

      {/* Lignes existantes */}
      {enriched.length > 0 ? (
        <div className="space-y-1.5">
          {enriched.map(item => {
            const db = DIR_BADGE[item.direction] || DIR_BADGE.both;
            return (
              <div key={item.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border"
                style={{ borderColor: 'var(--border)' }}>
                <span className="text-xs font-medium flex-1 truncate" style={{ color: 'var(--text)' }}>{item.source_name}</span>
                {isFamille && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0"
                    style={{ background: db.bg, color: db.color }}>{db.label}</span>
                )}
                <ChevronRight size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <span className="text-xs font-semibold flex-shrink-0" style={{ color: 'var(--primary-solid)' }}>
                  {item.category_name || `#${item.category_id}`}
                </span>
                {item.family_name && (
                  <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>({item.family_name})</span>
                )}
                <button onClick={() => handleDelete(item.id)}
                  className="p-1 rounded hover:bg-red-50 transition-colors ml-1 flex-shrink-0">
                  <Trash2 size={12} style={{ color: '#EF4444' }} />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Aucun mapping — ajoutez-en un ci-dessous.</p>
      )}

      {/* Formulaire d'ajout */}
      <div className="flex gap-2 items-end pt-1 flex-wrap">
        <div style={{ minWidth: 160, flex: '1 1 160px' }}>
          <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>
            {isFamille ? 'Famille d\'articles' : 'Agence'}
          </label>
          <select value={addSource} onChange={e => setAddSource(e.target.value)}
            className="w-full rounded-lg border px-2.5 py-1.5 text-xs focus:outline-none"
            style={{ borderColor: 'var(--border)' }}>
            <option value="">— Sélectionner —</option>
            {sources.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </div>
        {isFamille && (
          <div style={{ minWidth: 160, flex: '1 1 160px' }}>
            <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Sens</label>
            <select value={addDir} onChange={e => setAddDir(e.target.value)}
              className="w-full rounded-lg border px-2.5 py-1.5 text-xs focus:outline-none"
              style={{ borderColor: 'var(--border)' }}>
              {DIRECTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
        )}
        <div style={{ minWidth: 180, flex: '1 1 180px' }}>
          <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Catégorie Pennylane</label>
          <select value={addCat} onChange={e => setAddCat(e.target.value)}
            className="w-full rounded-lg border px-2.5 py-1.5 text-xs focus:outline-none"
            style={{ borderColor: 'var(--border)' }}>
            <option value="">— Sélectionner —</option>
            {categories.filter(c => c.type === 'category').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <button onClick={handleAdd} disabled={saving || !addSource || !addCat}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50 flex-shrink-0"
          style={{ background: 'var(--primary-solid)' }}>
          <Check size={12} /> {saving ? '…' : 'Ajouter'}
        </button>
      </div>
    </div>
  );
}

function MappingsTab({ societeId }) {
  const [mappings,  setMappings]  = useState([]);
  const [categories, setCategories] = useState([]);
  const [agences,   setAgences]   = useState([]);
  const [familles,  setFamilles]  = useState([]);
  const [loading,   setLoading]   = useState(true);

  const load = useCallback(async () => {
    if (!societeId) return;
    setLoading(true);
    try {
      const [mR, cR, sR, fR] = await Promise.all([
        api.getPlCategoryMappings(societeId),
        api.getPlCategories(societeId),
        api.getSocietes(),
        api.getFamilles(),
      ]);
      setMappings(mR.data || []);
      setCategories(cR.data || []);
      // Aplatir agences depuis toutes les sociétés
      const allAgences = (sR.data || []).flatMap(s =>
        (s.agences || []).map(a => ({ id: a.agence_id, nom: `${a.nom} (${s.nom})` }))
      );
      setAgences(allAgences);
      setFamilles((fR.data || []).map(f => ({ id: f.famille_id, nom: f.nom })));
    } catch {}
    finally { setLoading(false); }
  }, [societeId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Chargement…</p>;

  return (
    <div className="space-y-6">
      <MappingsSection societeId={societeId} sourceType="agence" sourceLabel="Mappings Agences"
        sources={agences} categories={categories} mappings={mappings} onRefresh={load} />
      <div className="border-t" style={{ borderColor: 'var(--border)' }} />
      <MappingsSection societeId={societeId} sourceType="famille" sourceLabel="Mappings Familles d'articles"
        sources={familles} categories={categories} mappings={mappings} onRefresh={load} />
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function PlCategoriesConfig() {
  const [societes,   setSocietes]   = useState([]);
  const [societeId,  setSocieteId]  = useState('');
  const [activeTab,  setActiveTab]  = useState('catalogue');

  useEffect(() => {
    api.getSocietes().then(r => {
      const s = r.data || [];
      setSocietes(s);
      if (s.length === 1) setSocieteId(s[0].societe_id);
    }).catch(() => {});
  }, []);

  const TABS = [
    { key: 'catalogue', label: 'Catalogue PL' },
    { key: 'mappings',  label: 'Mappings ERP ↔ PL' },
  ];

  return (
    <div className="space-y-4">
      {/* Sélecteur société */}
      {societes.length > 1 && (
        <div className="flex items-center gap-3">
          <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Société Pennylane</label>
          <select value={societeId} onChange={e => setSocieteId(e.target.value)}
            className="rounded-lg border px-2.5 py-1.5 text-xs focus:outline-none"
            style={{ borderColor: 'var(--border)' }}>
            <option value="">— Sélectionner —</option>
            {societes.map(s => <option key={s.societe_id} value={s.societe_id}>{s.nom}</option>)}
          </select>
        </div>
      )}

      {/* Sous-onglets */}
      <div className="flex gap-1 border-b" style={{ borderColor: 'var(--border)' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className="px-4 py-2 text-xs font-semibold transition-colors"
            style={activeTab === t.key
              ? { color: 'var(--primary-solid)', borderBottom: '2px solid var(--primary-solid)', marginBottom: -1 }
              : { color: 'var(--text-muted)' }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="pt-1">
        {activeTab === 'catalogue' && <CatalogueTab societeId={societeId} />}
        {activeTab === 'mappings'  && <MappingsTab  societeId={societeId} />}
      </div>
    </div>
  );
}
